import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatFormField } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { SingleUserPermissionsActionData } from 'src/app/models/api/actions/userPermissions.model';
import { ZenityEntityActionResultCode } from 'src/app/models/api/actions/zenityEntityActions';
import { ParamsForActions } from 'src/app/services/actions/common/common-actions.service';
import {
  ActionsHttpServiceService,
  RequiredParamsForSetAccessHttpRequests,
} from 'src/app/services/httpServices/actionsHttpService/actions-http-service.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { Label } from 'src/app/labels/englishLabels';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { LucideIconsModule } from 'src/app/shared/icons/lucide-icons.module';
import { TruncateTooltipDirective } from 'src/app/directives/truncate-tooltip/truncate-tooltip.directive';
import {
  ConfirmationDialogComponent,
  ConfirmData,
} from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { FabricAccessLevel } from 'src/app/services/httpServices/actionsHttpService/actions-http-service.service';
import { SetFabricResourcePermissionsActionInputs } from 'src/app/services/httpServices/actionsHttpService/actions-http-service.service';
import { AppValidators } from 'src/app/common/appValidators';

export enum FabricUserAccessLevel {
  admin = 'Admin (Owner)',
  member = 'Member (Can Edit)',
  contributor = 'Contributor (Can Edit)',
  viewer = 'Viewer (Read)',
}

export interface EnhancedUserPermission extends SingleUserPermissionsActionData {
  isUpdating?: boolean;
  originalRole?: FabricAccessLevel;
}

export interface RoleOption {
  value: FabricAccessLevel | 'remove';
  label: string;
}

@Component({
  selector: 'app-action-set-access-enhanced',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatTooltipModule,
    LabelsProviderPipe,
    LucideIconsModule,
    TruncateTooltipDirective,
  ],
  templateUrl: './action-set-access-enhanced.component.html',
  styleUrl: './action-set-access-enhanced.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActionSetAccessEnhancedComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<ActionSetAccessEnhancedComponent>);
  private readonly data = inject<ParamsForActions<RequiredParamsForSetAccessHttpRequests>>(MAT_DIALOG_DATA);
  private readonly actionsHttpService = inject(ActionsHttpServiceService);
  private readonly notificationsService = inject(NotificationsService);
  private readonly logger = inject(LoggerService);
  private readonly matDialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  readonly users = signal<EnhancedUserPermission[]>([]);
  readonly searchTerm = signal('');
  readonly isLoading = signal(false);
  readonly isInitialLoading = signal(true);
  readonly isBulkUpdating = signal(false);
  readonly emailFormControl = new FormControl('', [Validators.required, AppValidators.email]);
  readonly newUserRole = signal<FabricAccessLevel | null>(null);
  readonly isAddingUser = signal(false);

  // Search subject for debouncing
  private readonly searchSubject = new Subject<string>();

  readonly roleOptions: RoleOption[] = [
    { value: FabricAccessLevel.admin, label: FabricUserAccessLevel.admin },
    { value: FabricAccessLevel.member, label: FabricUserAccessLevel.member },
    {
      value: FabricAccessLevel.contributor,
      label: FabricUserAccessLevel.contributor,
    },
    { value: FabricAccessLevel.viewer, label: FabricUserAccessLevel.viewer },
    { value: 'remove', label: 'Remove User Access' },
  ];

  private readonly emailStatus = toSignal(this.emailFormControl.statusChanges, { initialValue: 'INVALID' });

  readonly isAddUserValid = computed(() => this.emailStatus() === 'VALID' && this.newUserRole() !== null);

  readonly filteredUsers = computed(() => {
    const term = this.searchTerm().toLowerCase();
    const allUsers = this.users();

    if (!term) return allUsers;

    return allUsers.filter(
      (user) => user.displayName?.toLowerCase().includes(term) || user.email?.toLowerCase().includes(term)
    );
  });

  readonly canIsolateResource = computed(() => {
    const allUsers = this.users();
    return allUsers.some((user) => user.role !== FabricAccessLevel.admin);
  });

  readonly canBulkEdit = computed(() => {
    return this.users().length > 0;
  });

  readonly bulkEditDisabledReason = computed(() => {
    if (this.users().length === 0) {
      return 'No users available';
    }
    return '';
  });

  readonly bulkRoleOptions = computed(() => {
    return this.roleOptions.filter((opt) => opt.value !== 'remove');
  });

  ngOnInit(): void {
    this.dialogRef.disableClose = true;
    this.dialogRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.close();
      });

    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((term) => this.searchTerm.set(term));

    this.loadUserPermissions();
  }

  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchSubject.next(input.value);
  }

  getUserInitials(user: EnhancedUserPermission): string {
    if (user.displayName) {
      const parts = user.displayName.split(' ');
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      return user.displayName.substring(0, 2).toUpperCase();
    }
    const email = user.email || user.identityPlatformResourceId;
    return email.substring(0, 2).toUpperCase();
  }

  getRoleLabel(role: FabricAccessLevel): string {
    return this.roleOptions.find((r) => r.value === role)?.label || '';
  }

  getUserTooltip(user: EnhancedUserPermission): string {
    const email = user.email || user.identityPlatformResourceId;
    if (!user.displayName) return email;
    return `${user.displayName} (${email})`;
  }

  onSelectOpened(formField: MatFormField): void {
    requestAnimationFrame(() => {
      const panel = document.querySelector('.set-access-select-panel') as HTMLElement;
      if (panel) {
        const overlayPane = panel.closest('.cdk-overlay-pane') as HTMLElement;
        if (overlayPane) {
          const fieldElement = formField._elementRef.nativeElement as HTMLElement;
          const selectRect = fieldElement.getBoundingClientRect();
          overlayPane.style.transform = 'none';
          overlayPane.style.top = `${selectRect.bottom + 4}px`;
          overlayPane.style.left = `${selectRect.right - panel.offsetWidth}px`;
          panel.classList.add('panel-positioned');
        }
      }
    });
  }

  onSelectClosed(): void {
    const panel = document.querySelector('.set-access-select-panel.panel-positioned') as HTMLElement;
    if (panel) {
      panel.classList.remove('panel-positioned');
    }
  }

  async onIndividualRoleChange(user: EnhancedUserPermission, newValue: string): Promise<void> {
    if (newValue === 'remove') {
      await this.removeUserPermission(user);
    } else {
      await this.updateUserPermission(user, newValue as FabricAccessLevel);
    }
  }

  onBulkRoleChange(newValue: string): void {
    if (!this.canBulkEdit()) return;

    const confirmData: ConfirmData = {
      title: Label.get('commonActions.manageAccessAction.bulkRoleChangeConfirmTitle'),
      content: Label.get('commonActions.manageAccessAction.bulkRoleChangeConfirmContent'),
      positiveOptionText: Label.get('commonActions.manageAccessAction.bulkRoleChangeConfirmYes'),
      negativeOptionText: Label.get('commonActions.manageAccessAction.bulkRoleChangeConfirmNo'),
      icon: 'users',
    };

    const confirmDialogRef = this.matDialog.open(ConfirmationDialogComponent, {
      data: confirmData,
      disableClose: false,
      panelClass: 'confirmation-dialog-container',
    });

    confirmDialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.executeBulkRoleChange(newValue as FabricAccessLevel);
        }
      });
  }

  private executeBulkRoleChange(accessLevel: FabricAccessLevel): void {
    this.isBulkUpdating.set(true);

    this.actionsHttpService
      .setAllUsersAccess(this.data.requiredParams.integrationId, this.data.requiredParams.resourceId, accessLevel)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (response.resultCode === ZenityEntityActionResultCode.success) {
            const roleFromDescription = this.extractRoleFromDescription(response.description);
            const roleLabel = roleFromDescription || accessLevel;
            this.notificationsService.success(`All users role updated to "${roleLabel}"`);
            this.loadUserPermissions();
          } else {
            this.notificationsService.error(response.description || 'Failed to update all users');
          }
          this.isBulkUpdating.set(false);
        },
        error: (error) => {
          this.logger.error('Failed to update all users access', error);
          this.notificationsService.error('Failed to update all users');
          this.isBulkUpdating.set(false);
        },
      });
  }

  isolateResource(): void {
    if (!this.canIsolateResource()) return;

    const confirmData: ConfirmData = {
      title: Label.get('commonActions.manageAccessAction.isolateResourceConfirmTitle'),
      content: Label.get('commonActions.manageAccessAction.isolateResourceConfirmContent'),
      positiveOptionText: Label.get('commonActions.manageAccessAction.isolateResourceConfirmYes'),
      negativeOptionText: Label.get('commonActions.manageAccessAction.isolateResourceConfirmNo'),
      icon: 'shield-alert',
    };

    const confirmDialogRef = this.matDialog.open(ConfirmationDialogComponent, {
      data: confirmData,
      disableClose: false,
      panelClass: 'confirmation-dialog-container',
    });

    confirmDialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.executeIsolateResource();
        }
      });
  }

  private executeIsolateResource(): void {
    this.isLoading.set(true);

    this.actionsHttpService
      .isolateResource(this.data.requiredParams.integrationId, this.data.requiredParams.resourceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (response.resultCode === ZenityEntityActionResultCode.success) {
            this.notificationsService.success('Resource isolated successfully');
            this.loadUserPermissions();
          } else {
            this.notificationsService.error(response.description || 'Failed to isolate resource');
          }
          this.isLoading.set(false);
        },
        error: (error) => {
          this.logger.error('Failed to isolate resource', error);
          this.notificationsService.error('Failed to isolate resource');
          this.isLoading.set(false);
        },
      });
  }

  onNewUserRoleChange(role: FabricAccessLevel): void {
    this.newUserRole.set(role);
  }

  onAddUser(): void {
    if (!this.isAddUserValid()) return;

    this.isAddingUser.set(true);

    const inputs: SetFabricResourcePermissionsActionInputs = {
      identity_resource_type: 'user',
      identity_resource_id_kind: 'platformResourceId',
      identity_resource_id: this.emailFormControl.value ?? '',
      access_level: this.newUserRole() as FabricAccessLevel,
    };

    this.actionsHttpService
      .addUserAccess(this.data.requiredParams.integrationId, this.data.requiredParams.resourceId, inputs)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (response.resultCode === ZenityEntityActionResultCode.success) {
            const email = this.emailFormControl.value ?? '';
            this.notificationsService.success(`User ${email} added successfully`);
            this.emailFormControl.reset();
            this.newUserRole.set(null);
            this.loadUserPermissions();
          } else {
            this.notificationsService.error(response.description || 'Failed to add user');
          }
          this.isAddingUser.set(false);
        },
        error: (error) => {
          this.logger.error('Failed to add user', error);
          this.notificationsService.error('Failed to add user');
          this.isAddingUser.set(false);
        },
      });
  }

  close(): void {
    this.dialogRef.close();
  }

  trackByUserId(_index: number, user: EnhancedUserPermission): string {
    return user.identityPlatformResourceId;
  }

  private loadUserPermissions(): void {
    this.isInitialLoading.set(true);

    this.actionsHttpService
      .getUserAccessDetails(this.data.requiredParams.integrationId, this.data.requiredParams.resourceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (response.resultCode === ZenityEntityActionResultCode.success) {
            const enhancedUsers: EnhancedUserPermission[] = response.permissions.map((p) => ({
              ...p,
              isUpdating: false,
              originalRole: p.role,
            }));
            this.users.set(enhancedUsers);
          } else {
            this.notificationsService.error(response.description || 'Failed to load user permissions');
          }
          this.isInitialLoading.set(false);
        },
        error: (error: HttpErrorResponse) => {
          this.logger.error('Failed to load user permissions', error);
          this.handleHttpError(error, 'Failed to load user permissions');
          this.isInitialLoading.set(false);
        },
      });
  }

  private handleHttpError(error: HttpErrorResponse, fallbackMessage: string): void {
    const errorCode = error.error?.errorCode;
    const errorTitle = error.error?.title;

    if (
      errorCode === 'PLATFORM_API_FAILURE_RESOURCE_NOT_FOUND' ||
      (error.status === 422 && errorTitle === 'Platform resource not found')
    ) {
      const errorMsg = Label.get('commonActions.manageAccessAction.resourceNotFoundError');
      this.notificationsService.error(errorMsg);
    } else {
      const detail = error.error?.detail;
      const message = detail ? `${fallbackMessage}: ${detail}` : fallbackMessage;
      this.notificationsService.error(message);
    }
  }

  private extractRoleFromDescription(description: string | null): string | null {
    if (!description) return null;
    const match = description.match(/granted\s+(\w+)\s+access/i);
    return match ? match[1] : null;
  }

  private async updateUserPermission(
    user: EnhancedUserPermission,
    newRole: FabricAccessLevel,
    isBulkOperation = false
  ): Promise<boolean> {
    // Update local state to show loading
    if (!isBulkOperation) {
      this.users.update((users) =>
        users.map((u) =>
          u.identityPlatformResourceId === user.identityPlatformResourceId ? { ...u, isUpdating: true } : u
        )
      );
    }

    const inputs: SetFabricResourcePermissionsActionInputs = {
      identity_resource_type: 'user',
      identity_resource_id_kind: 'platformResourceId',
      identity_resource_id: user.identityPlatformResourceId,
      access_level: newRole,
    };

    return new Promise((resolve) => {
      this.actionsHttpService
        .setResourcePermissions(this.data.requiredParams.integrationId, this.data.requiredParams.resourceId, inputs)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            if (!isBulkOperation) {
              this.users.update((users) =>
                users.map((u) =>
                  u.identityPlatformResourceId === user.identityPlatformResourceId ? { ...u, isUpdating: false } : u
                )
              );
            }

            if (response.resultCode === ZenityEntityActionResultCode.success) {
              if (!isBulkOperation) {
                const roleLabel = this.getRoleLabel(newRole);
                this.notificationsService.success(`Role updated to "${roleLabel}"`);
                this.users.update((users) =>
                  users.map((u) =>
                    u.identityPlatformResourceId === user.identityPlatformResourceId ? { ...u, role: newRole } : u
                  )
                );
              }
              resolve(true);
            } else {
              if (!isBulkOperation) {
                this.notificationsService.error(response.description || 'Failed to update role');
              }
              resolve(false);
            }
          },
          error: (error) => {
            this.logger.error('Failed to update user permission', error);
            if (!isBulkOperation) {
              this.users.update((users) =>
                users.map((u) =>
                  u.identityPlatformResourceId === user.identityPlatformResourceId ? { ...u, isUpdating: false } : u
                )
              );
              this.notificationsService.error('Failed to update role');
            }
            resolve(false);
          },
        });
    });
  }

  private async removeUserPermission(user: EnhancedUserPermission, isBulkOperation = false): Promise<boolean> {
    // Update local state to show loading
    if (!isBulkOperation) {
      this.users.update((users) =>
        users.map((u) =>
          u.identityPlatformResourceId === user.identityPlatformResourceId ? { ...u, isUpdating: true } : u
        )
      );
    }

    return new Promise((resolve) => {
      this.actionsHttpService
        .removeResourcePermissions(
          this.data.requiredParams.integrationId,
          this.data.requiredParams.resourceId,
          user.identityPlatformResourceId
        )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            if (response.resultCode === ZenityEntityActionResultCode.success) {
              if (!isBulkOperation) {
                const userName = user.displayName || user.email || user.identityPlatformResourceId;
                this.notificationsService.success(`Access removed for ${userName}`);
                // Remove user from local state
                this.users.update((users) =>
                  users.filter((u) => u.identityPlatformResourceId !== user.identityPlatformResourceId)
                );
              }
              resolve(true);
            } else {
              if (!isBulkOperation) {
                this.users.update((users) =>
                  users.map((u) =>
                    u.identityPlatformResourceId === user.identityPlatformResourceId ? { ...u, isUpdating: false } : u
                  )
                );
                this.notificationsService.error(response.description || 'Failed to remove access');
              }
              resolve(false);
            }
          },
          error: (error) => {
            this.logger.error('Failed to remove user permission', error);
            if (!isBulkOperation) {
              this.users.update((users) =>
                users.map((u) =>
                  u.identityPlatformResourceId === user.identityPlatformResourceId ? { ...u, isUpdating: false } : u
                )
              );
              this.notificationsService.error('Failed to remove access');
            }
            resolve(false);
          },
        });
    });
  }
}
